import { getRecentSearches, RecentSearchesResponse } from "common/apis";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/useAuth";

/**
 * Hook to fetch all recent searches at once (with high perPage value)
 * Used for the expanded "All Recent Searches" view
 *
 * Keyed on the user for the same reason as `useRecentSearches`: a static key
 * caches one account's recents under a name the next account would read from.
 * Its five-minute `staleTime` hides the sign-in case that made the bug obvious
 * there, but not the case where one user signs out and another signs in inside
 * that window and is shown searches that are not theirs.
 *
 * @param enabled - Whether to fetch data (only enabled when expanded view is active)
 */
export const useAllRecentSearches = (enabled: boolean = false) => {
  const { user } = useAuth();

  return useQuery<RecentSearchesResponse, Error>({
    queryKey: ["allRecentSearches", user?.id],
    queryFn: async () => {
      const token = user?.accessToken || "";
      return getRecentSearches(token, 1, 100, "recent");
    },
    // Only fetch when the expanded view is active, and only for someone whose
    // recents we can actually ask for.
    enabled: enabled && !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
};
